/**
 * Seamless fetch wrapper that automatically renews the backend auth cookie on 401 responses.
 * If a request returns 401, it will refresh the Firebase ID token, call loginWithFirebase,
 * and retry the original request once. If it still fails, returns the 401 response.
 * Usage: Replace all fetch() calls in exported API functions with fetchWithRenewal().
 */
export async function fetchWithRenewal(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const attemptRequest = async (retry = false): Promise<Response> => {
    let response = await fetch(input, init);
    if (response.status === 401 && !retry) {
      try {
        const loginResult = await loginWithFirebase();
        if (loginResult && loginResult.error) {
          // If login fails, return the original 401 response
          return response;
        }
        // After successful login, retry the original request
        return await attemptRequest(true);
      } catch (err) {
        // If any part of the renewal process fails, return the original 401
        return response;
      }
    }
    return response;
  };

  return attemptRequest();
}

// Auth check endpoint
export const checkAuth = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/auth/check`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  console.log('[checkAuth] status:', response.status);
  if (!response.ok) {
    console.warn('[checkAuth] Not authenticated, throwing error');
    throw new Error('Not authenticated');
  }
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    console.warn('[checkAuth] Response JSON parse error:', e);
    data = null;
  }
  console.log('[checkAuth] response data:', data);
  return data;
};

// Update login/logout endpoints
export async function logoutWithBackend() {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/auth/logout`;
  try {
    const response = await fetchWithRenewal(apiURL, {
      method: 'POST',
      credentials: 'include', // ensures cookie is cleared
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Logout failed' }));
      return { error: true, message: errorData.message || 'Logout failed' };
    }
    return await response.json();
  } catch (err: any) {
    return { error: true, message: err?.message || 'Logout failed' };
  }
}

export async function loginWithFirebase() {
  // The `getIdToken(true)` method automatically handles refreshing the token if it's expired.
  const idToken = await auth.currentUser?.getIdToken?.(true);

  if (!idToken) {
    // This can happen if the user is signed out or if the refresh fails.
    return { error: true, message: 'No Firebase ID token found. Please log in again.' };
  }

  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/auth/login`;
  try {
    // Use a direct fetch call here to avoid recursion with fetchWithRenewal
    const response = await fetch(apiURL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
      credentials: 'include', // ensures cookie is set
    });

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      // If the backend still says the token is expired, there might be a clock skew issue.
      const errorMessage = typeof data === 'string' ? data : data?.message || 'Login failed';
      if (errorMessage.includes('ID token has expired')) {
        console.error('Backend rejected a freshly issued token as expired. Check for clock skew between client and server.');
      }
      return { error: true, message: errorMessage };
    }

    // If success, return parsed data or text
    if (typeof data === 'string') {
      return { success: true, message: data };
    }
    return data;
  } catch (err: any) {
    return { error: true, message: err?.message || 'Login failed' };
  }
}
/**
 * Returns the proxied video stream URL for playback, using the backend endpoint.
 * @param videoId The ID of the video
 */
export function getVideoStreamUrl(videoId: string) {
  // Use the backend base URL from env, not relative/localhost
  const base = process.env.REACT_APP_BACKEND_BASE_URL;
  return `${base}/videos/${videoId}/stream`;
}
import { isDevOrTestEnv } from './env';
import { auth } from './auth/firebase-service';
// Debug: Managed mode toggle (only in dev/test/local)
let debugGetManaged: (() => Promise<boolean>) | undefined;
let debugSetManaged: ((managed: boolean) => Promise<void>) | undefined;
if (isDevOrTestEnv()) {
  const debugEndpoint = `${process.env.REACT_APP_BACKEND_BASE_URL}/debug/managed`;
  debugGetManaged = async () => {
    const token = await auth.currentUser?.getIdToken?.(true);
    if (!token) throw new Error('Authorization header missing');
    const res = await fetchWithRenewal(debugEndpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to get managed mode');
    const data = await res.json();
    return !!data.managed;
  };
  debugSetManaged = async (managed: boolean) => {
    const token = await auth.currentUser?.getIdToken?.(true);
    if (!token) throw new Error('Authorization header missing');
    const res = await fetchWithRenewal(debugEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ managed }),
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to set managed mode');
  };
}
export { debugGetManaged, debugSetManaged };
// Cancel Stripe subscription at renewal date
export const cancelStripeSubscription = async (subscriptionId: string) => {
  const token = await auth.currentUser?.getIdToken(true);
  if (!token) throw new Error('You must be logged in to cancel a subscription.');
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/subscription/cancel`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ subscriptionId }),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to cancel subscription' }));
    throw new Error(errorData.message);
  }
  return response.json();
};
import axios from 'axios';
// Reactivate Stripe subscription (undo cancellation)
export const reactivateStripeSubscription = async (subscriptionId: string) => {
  const token = await auth.currentUser?.getIdToken(true);
  if (!token) throw new Error('You must be logged in to reactivate a subscription.');
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/subscription/reactivate`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ subscriptionId }),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to reactivate subscription' }));
    throw new Error(errorData.message);
  }
  return response.json();
};
import {
  Team,
  TeamUser,
  TeamInviteRequest,
  TeamInviteResponse,
  TeamRoleUpdateRequest,
  TeamOwnershipTransferRequest,
  TeamTokenPurchaseRequest,
  TeamTokenAllocateRequest,
  TeamTokenDistributeResponse,
  TeamProjectAssignRequest,
  TeamProjectUnassignRequest,
} from '../types/Team';
import { convertSchema2 } from './APIDataHelpers';

// -------------------- USER --------------------
export const getProfile = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/profile`;
  /**
   * Distribute tokens from the team’s pool to individual users (team admins/owners only).
   * Use this when you want to specify exactly how many tokens each user should receive.
   * @param teamId The team ID
   * @param distribution Array of { userId, amount }
   * @returns API response
   */
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  const { data } = await response.json();
  return data;
};

export const getUserData = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/data`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch user data');
  /**
   * Evenly distribute all available tokens among team members (optionally including the owner).
   * Use this for one-click even distribution (e.g. with an 'includeOwner' flag).
   * @param teamId The team ID
   * @param body Optional: { includeOwner: boolean }
   * @returns API response
   */
  return await response.json();
};

export const updateUserData = async (
  data: Partial<{
    title: string;
    first: string;
    second: string;
    company: string;
    role: string;
    accountType: string;
    intention: string;
  }>
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/data`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to update user data');
  return await response.json();
};

// -------------------- VIDEO PROCESSING --------------------
export const getVideos = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  const { data } = await response.json();
  return data;
};

export const getSDKData = async (videoId: string, fields?: string[]) => {
  try {
    let apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}/data`;
    if (fields && fields.length > 0) {
      apiURL += `?fields=${encodeURIComponent(fields.join(','))}`;
    }
    const token = await auth.currentUser?.getIdToken(true);
    if (!token) throw new Error('User is not authenticated.');
    const response = await fetchWithRenewal(apiURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    if (response.status === 204) return null;
    if (!response.ok) {
      return null;
    }
    let jsonResponse;
    try {
      jsonResponse = await response.json();
    } catch (e) {
      return null;
    }
    if (!jsonResponse || typeof jsonResponse !== 'object') {
      return null;
    }
    const rawData = jsonResponse.data;
    if (!rawData) return null;
    const data = {
      ...rawData,
      ...(rawData?.graph_status && {
        graph_stats: rawData.graph_status,
        graph_status: undefined,
      }),
    };
    if (data && 'sv' in data && data['sv'] == 2) {
      const convertedData = convertSchema2(data);
      if (!convertedData) return null;
      return convertedData;
    }
    return data;
  } catch (error) {
    console.error('[getSDKData] Error:', error);
    return null;
  }
};

// -------------------- PROJECT MANAGEMENT --------------------
export const createProject = async (title: string, description: string, teamId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects`;
  const payload: { title: string; description: string; teamId: string } = { title, description, teamId };

  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  return await response.json();
};

export const deleteProject = async (
  projectId: string,
  deleteVideos: boolean = false
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}?deleteVideos=${deleteVideos}`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'DELETE',
    headers: {
    },
    credentials: 'include',
  });
  return await response.json();
};

export const updateProject = async (
  projectId: string,
  data: { title?: string; description?: string }
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}`;
  const body: { title?: string; description?: string } = {};
  if (data.title && typeof data.title === 'string' && data.title.trim() !== '')
    body.title = data.title;
  if (
    data.description &&
    typeof data.description === 'string' &&
    data.description.trim() !== ''
  )
    body.description = data.description;
  const response = await fetchWithRenewal(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  return await response.json();
};

export const shareProject = async (projectId: string, email: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/share`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
    credentials: 'include',
  });
  return await response.json();
};

export const getJobs = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/jobs`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  const { data } = await response.json();
  return data;
};

// -------------------- PROJECTS --------------------
export const listProjects = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects`;
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const getProject = async (projectId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}`;
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const allocateVideo = async (projectId: string, videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/allocate`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify({ videoId }),
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const deallocateVideo = async (projectId: string, videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/deallocate`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify({ videoId }),
    credentials: 'include',
  });
  return (await response.json()).data;
};

// -------------------- VIDEOS --------------------
export const listVideos = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos`;
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const getVideo = async (videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}`;
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {

    },
    credentials: 'include',
  });
  return await response.blob();
};

export const updateVideo = async (videoId: string, data: any) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}`;
  const response = await fetch(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const deleteVideo = async (videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}`;
  const response = await fetch(apiURL, {
    method: 'DELETE',
    headers: {
    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const getVideoData = async (videoId: string, fields?: string[]) => {
  let apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}/data`;
  if (fields && fields.length > 0) {
    apiURL += `?fields=${encodeURIComponent(fields.join(','))}`;
  }
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const restoreVideo = async (videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}/restore`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {

    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

// Video upload helpers
export const getVideoUploadLink = async (file: File, projectId: string, clientId?: string) => {
  const formData = new FormData();
  formData.append('fileName', file.name);
  formData.append('projectId', projectId);
  if (clientId) formData.append('clientId', clientId);
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
    },
    credentials: 'include',
    body: formData,
  });
  return (await response.json()).data;
};

export const uploadFileToSignedUrl = async (
  signedURL: string,
  file: File,
  progressCallback: (percent: number) => void,
  abortSignal?: AbortSignal
) => {
  try {
    const response = await axios.put(signedURL, file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.lengthComputable) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total!
          );
          progressCallback(percentCompleted);
        }
      },
      signal: abortSignal,
    });
    return {
      success: true,
      message: 'Upload completed successfully',
      data: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      message: 'Error uploading file: ' + error.message,
    };
  }
};

// -------------------- CATEGORIES --------------------
export const getCategories = async (projectId: string, shared?: boolean) => {
  let apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/categories`;
  if (shared) {
    apiURL += '?shared=true';
  }
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return (await response.json()).data.categories;
};

export const addCategory = async (
  projectId: string,
  data: { name: string; shared?: boolean }
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/categories`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const updateCategory = async (
  projectId: string,
  categoryId: string,
  data: { name?: string; shared?: boolean }
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/categories/${categoryId}`;
  const response = await fetch(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const removeCategory = async (projectId: string, categoryId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/categories/${categoryId}`;
  const response = await fetch(apiURL, {
    method: 'DELETE',
    headers: {
    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

// -------------------- TAGS --------------------
export const getTags = async (
  projectId: string,
  categoryId: string,
  shared?: boolean
) => {
  let apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/categories/${categoryId}/tags`;
  if (shared) {
    apiURL += '?shared=true';
  }
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return (await response.json()).data.tags;
};

export const addTag = async (
  projectId: string,
  categoryId: string,
  data: { name: string; videoIds?: string[]; shared?: boolean }
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/categories/${categoryId}/tags`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const updateTag = async (
  projectId: string,
  categoryId: string,
  tagId: string,
  data: { name?: string; videoIds?: string[]; shared?: boolean }
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/categories/${categoryId}/tags/${tagId}`;
  const response = await fetch(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const removeTag = async (
  projectId: string,
  categoryId: string,
  tagId: string
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/categories/${categoryId}/tags/${tagId}`;
  const response = await fetch(apiURL, {
    method: 'DELETE',
    headers: {
    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

export const associateTag = async (
  tagId: string,
  videoIds: string[],
  action: string
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/tags/${tagId}/associate`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify({ videoIds, action }),
    credentials: 'include',
  });
  return (await response.json()).data;
};

// revokeProjectAccess
export const revokeProjectAccess = async (
  projectId: string,
  userId: string,
  pending: boolean
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/revoke`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify({ userId, pending }),
    credentials: 'include',
  });
  return await response.json();
};

// getProjectAuthorisedList
export const getProjectAuthorisedList = async (projectId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/authorised`;
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

// getProjectLayout
export const getProjectLayout = async (projectId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/layout`;
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return await response.json();
};

// updateProjectLayout
export const updateProjectLayout = async (
  projectId: string,
  filters: any[]
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/layout`;
  const response = await fetch(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',

    },
    body: JSON.stringify({ filters }),
    credentials: 'include',
  });
  return (await response.json()).data;
};

// getUploadProgress (WebSocket logic for upload progress tracking)
export const getUploadProgress = async (
  jobID: string,
  videoID: string,
  progressCallback: (jobID: string, data: any) => void,
  onCloseCallback: (videoID: string, data: any) => void
) => {
  let count = 0;
  let heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
  const HEARTBEAT_MS = 12000; // 12 seconds

  const connectWebSocket = async () => {
    let completed = false;
    const token = await auth.currentUser?.getIdToken();
    const socket = new WebSocket(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/jobs/${jobID}/progress?auth=${token}`
    );

    const resetHeartbeat = () => {
      if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
      heartbeatTimeout = setTimeout(() => {
        console.warn(`[WebSocket][${jobID}] No message in ${HEARTBEAT_MS / 1000}s, treating as lost. Closing and reconnecting.`);
        try { socket.close(); } catch (e) {}
        // reconnect will be triggered by onclose
      }, HEARTBEAT_MS);
    };

    socket.onopen = () => {
      console.log(`[WebSocket][${jobID}] Connection opened.`);
      resetHeartbeat();
    };

    socket.onmessage = (event) => {
      resetHeartbeat();
      let data = JSON.parse(event.data);
      if (count === 4) {
        data = { ...data, status: 'FAILED' };
      }
      progressCallback(jobID, data);
      if (data.complete) {
        completed = true;
        if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
        socket.close();
        onCloseCallback(videoID, data);
      }
    };

    socket.onerror = (error) => {
      console.error(`[WebSocket][${jobID}] Error:`, error);
      if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
      socket.close();
    };

    socket.onclose = (event) => {
      if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
      if (!event.wasClean && !completed) {
        console.log(`[WebSocket][${jobID}] Closed unexpectedly (code ${event.code}). Reconnecting...`);
        reconnect();
      } else {
        console.log(`[WebSocket][${jobID}] Connection closed. Completed:`, completed);
      }
    };
  };

  const reconnect = async () => {
    count += 1;
    await new Promise((resolve) => setTimeout(resolve, 2000));
    await connectWebSocket();
  };

  await connectWebSocket();
};

// cancelUpload
export const cancelUpload = async (jobId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/jobs/${jobId}/cancel`;
  const token = await auth.currentUser?.getIdToken(true);
  if (!token) throw new Error('User is not authenticated.');
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to cancel upload');
  return (await response.json()).data;
};

// notifyServer
export const notifyServer = async (
  jobID: string,
  videoID: string,
  fileName: string,
  projectID: string
) => {
  const formData = new FormData();
  formData.append('videoID', videoID);
  formData.append('fileName', fileName);
  if (projectID) formData.append('projectID', projectID);
  await fetchWithRenewal(
    `${process.env.REACT_APP_BACKEND_BASE_URL}/jobs/${jobID}/notify`,
    {
      method: 'POST',
      body: formData,
      headers: {
      },
      credentials: 'include',
    }
  );
};

// markVideoForDeletion
export const markVideoForDeletion = async (videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'DELETE',
    headers: {
    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

// unmarkVideoFromDeletion
export const unmarkVideoFromDeletion = async (videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}/restore`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

// permanentlyDeleteVideo
export const permanentlyDeleteVideo = async (videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}/erase`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'DELETE',
    headers: {
    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

// linkToProject
export const linkToProject = async (projectId: string, videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/allocate`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ videoId }),
    credentials: 'include',
  });
  return (await response.json()).data;
};

// unlinkFromProject
export const unlinkFromProject = async (projectId: string, videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/deallocate`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ videoId }),
    credentials: 'include',
  });
  return (await response.json()).data;
};

// sendInformationForm
export const sendInformationForm = async (formInputs: {
  title: string;
  firstName: string;
  surname: string;
  company: string;
  role: string;
  intention: string;
}) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/data`;
  const response = await fetch(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
    body: JSON.stringify({
      title: formInputs.title,
      first: formInputs.firstName,
      second: formInputs.surname,
      company: formInputs.company,
      role: formInputs.role,
      intention: formInputs.intention,
    }),
  });
  if (!response.ok) throw new Error('Failed to update user data');
  return await response.json();
};

// sendEmailEnquiry
export const sendEmailEnquiry = async (enquiryData: any) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/contact/enquiry`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(enquiryData),
  });
  return response;
};

// resumeProcessingQueue
export const resumeProcessingQueue = async (userId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/users/${userId}/jobs/resume`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  return (await response.json()).data;
};

// fetchAnnotationsForVideo
export const fetchAnnotationsForVideo = async (videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/annotations?videoId=${videoId}`;
  const response = await fetch(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
  });
  const json = await response.json();
  return json.data?.annotations || [];
};

// addAnnotationToVideo
export const addAnnotationToVideo = async (
  videoId: string,
  startFrame: number,
  endFrame: number,
  annotation: string
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/annotations`;
  const response = await fetch(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
    body: JSON.stringify({
      videoId,
      startFrame,
      endFrame,
      description: annotation,
    }),
  });
  return await response.json();
};

// deleteAnnotation
export const deleteAnnotation = async (annotationId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/annotations/${annotationId}`;
  const response = await fetch(apiURL, {
    method: 'DELETE',
    headers: {

    },
    credentials: 'include',
  });
  return await response.json();
};

// updateAnnotation
export const updateAnnotation = async (
  annotationId: string,
  data: { startFrame?: number; endFrame?: number; description?: string }
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/annotations/${annotationId}`;
  const response = await fetch(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',

    },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return await response.json();
};

export const getThumbnail = async (videoId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/videos/${videoId}/thumbnail`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
    },
    credentials: 'include',
  });
  return await response.blob();
};

// Accept a project invitation
export const acceptProjectInvitation = async (code: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/accept`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
    credentials: 'include',
  });
  return await response.json();
};

export const getSubscription = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/subscriptions`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  return await response.json();
};

export const getLatestSubscription = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/subscriptions`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  const json = await response.json();
  if (json.data && json.data.subscriptions) {
    const subscriptionId = Object.keys(json.data.subscriptions)[0];
    if (subscriptionId) {
      return json.data.subscriptions[subscriptionId];
    }
  }
  return null;
};

export const getSubscriptionDetails = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/subscription/details`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  const json = await response.json();
  return json.data;
};

export const getSubscriptionTiers = async () => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/subscription/tiers`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch subscription tiers');
  const json = await response.json();
  return json.data || [];
};

//Get Project groups
export const getProjectGroups = async (projectId: string) => {
  try {
    let apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/groups`;
    const response = await fetchWithRenewal(apiURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    return (await response.json()).data;
  } catch (error) {
    console.log('error getting project groups', error);
  }
};

//create project group
export const createProjectGroup = async (data: {
  projectId: string;
  name: string;
  color: string;
  measurability: string[];
  interestingEvents: { min: number; max: number };
  videoDurationInFrames: { min: number; max: number };
  customTags: { tagId: string; enabled: boolean }[];
}) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${data.projectId}/groups`;

  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      name: data.name,
      color: data.color,
      measurability: data.measurability,
      interestingEvents: data.interestingEvents,
      videoDurationInFrames: data.videoDurationInFrames,
      customTags: data.customTags,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create project group: ${response.status} - ${errorText}`
    );
  }

  return await response.json();
};

export const updateProjectGroup = async (
  projectId: string,
  groupId: string,
  updates: Partial<{
    name: string;
    color: string;
    measurability: string[];
    interestingEvents: { min: number; max: number };
    videoDurationInFrames: { min: number; max: number };
    customTags: { tagId: string; enabled: boolean }[];
  }>
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/groups/${groupId}`;

  const response = await fetchWithRenewal(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to patch project group: ${response.status} - ${errorText}`
    );
  }

  return await response.json();
};

export const deleteProjectGroup = async (
  projectId: string,
  groupId: string
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/${projectId}/groups/${groupId}`;

  const response = await fetchWithRenewal(apiURL, {
    method: 'DELETE',
    headers: {
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to delete project group: ${response.status} - ${errorText}`
    );
  }

  return true;
};

// -------------------- TEAM MANAGEMENT --------------------


// Distribute tokens from the team’s pool to individual users (team admins/owners only)
export const distributeTeamTokensToUsers = async (
  teamId: string,
  distribution: { userId: string; amount: number }[]
) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/tokens/distribute`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ distribution }),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message || `Failed to distribute tokens: ${response.statusText}`);
  }
  return await response.json();
};

// Get a summary of the team’s token state (current, rollover, history)
export const getTeamTokenState = async (teamId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/tokens`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch team token state');
  return (await response.json()).data;
};

// Get a log of all token usage events for a user
export const getUserTokenUsage = async (userId: string) => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/users/${userId}/token-usage`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch user token usage');
  return (await response.json()).data;
};

/**
 * Fetches user data for a given user ID.
 * @param userId The ID of the user to fetch.
 * @returns A promise that resolves with the user's data.
 */
export const getUserById = async (userId: string): Promise<{ email: string;[key: string]: any }> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/users/${userId}`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) {
    // Try to parse the error, but have a fallback
    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch user data' }));
    throw new Error(errorData.message || `Failed to fetch user with status: ${response.status}`);
  }
  return (await response.json()).data;
};

export const createTeam = async (name: string): Promise<Team> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw new Error('Failed to create team');
  return (await response.json()).data;
};

export const listTeamUsers = async (teamId: string): Promise<TeamUser[]> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/users`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to list team users');
  return (await response.json()).data;
};

export const getTeamUser = async (teamId: string, userId: string): Promise<TeamUser> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/users/${userId}`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to get team user');
  return (await response.json()).data;
};

export const inviteTeamUser = async (teamId: string, invite: TeamInviteRequest): Promise<TeamInviteResponse> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/invite`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(invite),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to invite user and could not parse error' }));
    throw new Error(errorData.message || 'Failed to invite user');
  }
  return await response.json();
};

export const updateTeamUserRole = async (teamId: string, userId: string, update: TeamRoleUpdateRequest): Promise<TeamUser> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/users/${userId}`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(update),
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to update user role');
  return (await response.json()).data;
};

export const removeTeamUser = async (teamId: string, userId: string): Promise<{ success: boolean }> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/users/${userId}`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'DELETE',
    headers: {
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to remove user from team');
  return await response.json();
};

export const transferTeamOwnership = async (teamId: string, transfer: TeamOwnershipTransferRequest): Promise<{ success: boolean }> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/transfer-ownership`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(transfer),
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to transfer ownership');
  return await response.json();
};

export const purchaseTeamTokens = async (
  teamId: string,
  purchase: TeamTokenPurchaseRequest
): Promise<{ success: boolean; tokens: number; checkoutUrl: string }> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/tokens/purchase`;
  const payload: any = { tokens: purchase.tokens };
  if (purchase.returnUrl) payload.returnUrl = purchase.returnUrl;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  if (!response.ok) {
    // Try to extract error text from backend response
    let errorText = '';
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = 'Failed to purchase tokens';
    }
    // If errorText is JSON, try to parse message
    try {
      const errorJson = JSON.parse(errorText);
      if (errorJson && errorJson.message) {
        errorText = errorJson.message;
      }
    } catch (e) {
      // errorText is plain text
    }
    throw new Error(errorText || 'Failed to purchase tokens');
  }
  // Expecting { success, tokens, checkoutUrl } from backend
  const result = await response.json();
  return {
    success: result.success,
    tokens: result.tokens,
    checkoutUrl: result.checkoutUrl,
  };
};

// TEAM MANAGEMENT
/**
 * Fetches the list of assignable projects for a team (owners/admins only).
 * @param teamId The team ID
 * @returns Array of assignable projects
 */
export const getAssignableProjectsForTeam = async (teamId: string): Promise<any[]> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/assignable-projects`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) throw new Error('Failed to fetch assignable projects');
  return (await response.json()).data;
};

export const distributeTeamTokens = async (
  teamId: string,
  body?: { distributions: { userId: string; tokens: number }[] }
): Promise<TeamTokenDistributeResponse> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/tokens/distribute`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    ...(body && { body: JSON.stringify(body) }), // Conditionally add body
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message || `Failed to distribute tokens: ${response.statusText}`);
  }
  return await response.json();
};

export const assignTeamProject = async (teamId: string, projectId: string, assign: { userId: string }): Promise<{ success: boolean }> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/projects/${projectId}/assign`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId: assign.userId }),
    credentials: 'include',
  });
  if (!response.ok) {
    let errorMsg = 'Failed to assign project';
    try {
      const data = await response.json();
      if (data && data.message) errorMsg = data.message;
    } catch {
      // fallback to text if not JSON
      try {
        const text = await response.text();
        if (text) errorMsg = text;
      } catch { }
    }
    throw new Error(errorMsg);
  }
  return { success: true };
};

export const unassignTeamProject = async (teamId: string, projectId: string, unassign: { userId: string }): Promise<{ success: boolean }> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/projects/${projectId}/unassign`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId: unassign.userId }),
    credentials: 'include',
  });
  if (!response.ok) {
    let errorMsg = 'Failed to unassign project';
    try {
      const data = await response.json();
      if (data && data.message) errorMsg = data.message;
    } catch {
      // fallback to text if not JSON
      try {
        const text = await response.text();
        if (text) errorMsg = text;
      } catch { }
    }
    throw new Error(errorMsg);
  }
  return { success: true };
};

export const getTeamsOwned = async (): Promise<Team[]> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/owned`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch owned teams');
  return (await response.json()).data;
};

export const getTeamsMember = async (): Promise<Team[]> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/member`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch member teams');
  return (await response.json()).data;
};

/**
 * Creates a new team.
 * @param {string} name - The name of the new team.
 * @returns {Promise<Team>} A promise that resolves with the created team.
 */
export const getTeamTokenSummary = async (teamId: string): Promise<{
  unassigned: number;
  perUser: { [userId: string]: number };
  unassignedBreakdown?: {
    monthly?: number;
    purchased?: number;
    rollover?: number;
  };
  assignedBreakdown?: {
    monthly?: number;
    purchased?: number;
    rollover?: number;
  };
  monthlyAllowance?: number;
}> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/${teamId}/tokens`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch team token summary');
  const data = (await response.json()).data;
  // Return all relevant fields including breakdowns if present
  return {
    unassigned: data.unassigned,
    perUser: data.perUser,
    unassignedBreakdown: data.unassignedBreakdown,
    assignedBreakdown: data.assignedBreakdown,
    monthlyAllowance: data.monthlyAllowance ?? data.monthly_allowance ?? data.monthly_tokens ?? (data.unassignedBreakdown?.monthly ?? data.assignedBreakdown?.monthly ?? undefined),
  };
};

// Accept a team invitation (for /accept-team page)
export const acceptTeamInvite = async (token: string): Promise<any> => {
  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/teams/invite/accept-team`;
  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
    credentials: 'include',
  });
  const responseData = await response.json();
  if (!response.ok) {
    const error = new Error(responseData.message || 'Failed to accept team invite.');
    (error as any).code = responseData.code;
    (error as any).response = responseData;
    throw error;
  }
  return responseData;
};

export const updateSubscription = async (payload: { newPriceId: string }) => {
  const token = await auth.currentUser?.getIdToken(true);
  if (!token) {
    throw new Error('You must be logged in to update a subscription.');
  }

  const apiURL = `${process.env.REACT_APP_BACKEND_BASE_URL}/user/subscription/update`;

  const response = await fetchWithRenewal(apiURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to update subscription' }));
    throw new Error(errorData.message);
  }

  return response.json();
};

